import { Text, Toggle } from '@amzn/meridian';
import type { ToggleProps } from '@amzn/meridian/toggle/toggle';
import { createUniqueElementId } from 'app.util';
import type { FieldValidator } from 'final-form';
import { type ReactNode, useCallback, useMemo } from 'react';
import { type UseFieldConfig, useField } from 'react-final-form';

type MFormToggleProps = {
  'aria-*'?: ToggleProps['aria-*']; // accept any aria-XYZ prop
  'data-*'?: ToggleProps['data-*']; // accept any data-XYZ prop
  className?: string;
  description?: string;
  disabled?: boolean;
  fieldConfigOverrides?: UseFieldConfig<boolean>;
  id?: string;
  label?: ReactNode;
  name: string;
  onChange?: (checked: boolean) => void;
  size?: ToggleProps['size'];
  validate?: FieldValidator<boolean>;
  value?: ToggleProps['value'];
};

/**
 * A wrapper for Meridian's Toggle component that integrates with Final Form.
 * Should only be used in a final form context. Use regular Meridian Toggle elsewhere
 */
export const MFormToggle = ({
  className,
  description,
  disabled,
  fieldConfigOverrides = {},
  id,
  label,
  name,
  onChange,
  size,
  validate,
  value,
  ...ariaAndDataProps // aria-* and data-* wildcard props are spread
}: MFormToggleProps) => {
  const inputId = useMemo(
    () => id ?? createUniqueElementId('toggle'),
    [
      id,
    ],
  );
  const descriptionId = useMemo(
    () => `${inputId}-description`,
    [
      inputId,
    ],
  );
  const errorMessageId = useMemo(
    () => `${inputId}-error-message`,
    [
      inputId,
    ],
  );

  const stableFieldConfig: UseFieldConfig<boolean> = useMemo(
    () => ({
      type: 'checkbox',
      ...fieldConfigOverrides,
      validate,
    }),
    [
      fieldConfigOverrides,
      validate,
    ],
  );

  const fieldProps = useField(name, stableFieldConfig);

  const handleOnChange = useCallback(
    (checked: boolean) => {
      fieldProps.input.onChange(checked);
      onChange?.(checked);
    },
    [
      fieldProps.input,
      onChange,
    ],
  );

  const describedByIds: string | undefined = useMemo(() => {
    // Include description ID and error message ID if they are present
    const newDescribedByIds: string[] = [];
    if (description) newDescribedByIds.push(descriptionId);
    if (fieldProps.meta.invalid) newDescribedByIds.push(errorMessageId);
    return newDescribedByIds.join(' ') || undefined;
  }, [
    description,
    descriptionId,
    errorMessageId,
    fieldProps.meta.invalid,
  ]);

  return (
    <div className={className}>
      <Toggle
        aria-describedby={describedByIds}
        aria-invalid={fieldProps.meta.invalid}
        data-cy={`checkbox-${name}`}
        {...ariaAndDataProps}
        checked={!!fieldProps.input.checked}
        disabled={disabled}
        id={inputId}
        onChange={handleOnChange}
        size={size}
        value={value}
      >
        {label}
      </Toggle>

      {fieldProps.meta.invalid && fieldProps.meta.error && (
        <Text color="error" id={errorMessageId} type="b200">
          {fieldProps.meta.error}
        </Text>
      )}

      {description && (
        <Text color="secondary" id={descriptionId} type="b200">
          {description}
        </Text>
      )}
    </div>
  );
};
